/**
 * Trunnion AI - Version Diff Component
 * Split view comparing two workflow versions.
 * Displays a summary of changes and node-level/edge-level diffs with color coding:
 *   Added = green, Removed = red, Changed = gold/amber
 */

import { useMemo } from 'react';
import {
  Plus,
  Minus,
  Pencil,
  ArrowRight,
  GitCompare,
  FileText,
} from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import type { WorkflowDefinition } from '@/types';
import { diffWorkflows } from '@/lib/workflowDiff';

interface VersionDiffProps {
  versionA: WorkflowDefinition;
  versionB: WorkflowDefinition;
  labelA?: string;
  labelB?: string;
}

function getNodeLabel(workflow: WorkflowDefinition, nodeId: string): string {
  const node = workflow.nodes.find((n) => n.id === nodeId);
  if (!node) return nodeId;
  const data = node.data as Record<string, unknown>;
  return (data.label as string) ?? nodeId;
}

function getNodeType(workflow: WorkflowDefinition, nodeId: string): string {
  const node = workflow.nodes.find((n) => n.id === nodeId);
  return node?.type ?? 'unknown';
}

function getEdgeLabel(workflow: WorkflowDefinition, edgeId: string): string {
  const edge = workflow.edges.find((e) => e.id === edgeId);
  if (!edge) return edgeId;
  const sourceLabel = getNodeLabel(workflow, edge.source);
  const targetLabel = getNodeLabel(workflow, edge.target);
  return `${sourceLabel} -> ${targetLabel}`;
}

export function VersionDiff({
  versionA,
  versionB,
  labelA = 'Version A',
  labelB = 'Version B',
}: VersionDiffProps) {
  const diff = useMemo(
    () => diffWorkflows(versionA, versionB),
    [versionA, versionB],
  );

  const totalChanges =
    diff.addedNodes.length +
    diff.removedNodes.length +
    diff.changedNodes.length +
    diff.addedEdges.length +
    diff.removedEdges.length +
    diff.metadataChanges.length;

  return (
    <div className="space-y-6">
      {/* Summary header */}
      <div className="rounded-lg border border-trunnion-slate-700 bg-trunnion-slate-900 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <GitCompare className="h-5 w-5 text-trunnion-gold-500" />
            <div>
              <h4 className="text-sm font-semibold text-white">Change Summary</h4>
              <p className="text-xs text-trunnion-slate-400 mt-0.5">
                {labelA} vs {labelB}
              </p>
            </div>
          </div>
          <Badge variant={totalChanges > 0 ? 'warning' : 'success'}>
            {totalChanges} change{totalChanges !== 1 ? 's' : ''}
          </Badge>
        </div>

        {totalChanges > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {diff.addedNodes.length > 0 && (
              <Badge variant="success">
                +{diff.addedNodes.length} node{diff.addedNodes.length !== 1 ? 's' : ''}
              </Badge>
            )}
            {diff.removedNodes.length > 0 && (
              <Badge variant="error">
                -{diff.removedNodes.length} node{diff.removedNodes.length !== 1 ? 's' : ''}
              </Badge>
            )}
            {diff.changedNodes.length > 0 && (
              <Badge variant="warning">
                ~{diff.changedNodes.length} node{diff.changedNodes.length !== 1 ? 's' : ''}
              </Badge>
            )}
            {diff.addedEdges.length > 0 && (
              <Badge variant="success">
                +{diff.addedEdges.length} edge{diff.addedEdges.length !== 1 ? 's' : ''}
              </Badge>
            )}
            {diff.removedEdges.length > 0 && (
              <Badge variant="error">
                -{diff.removedEdges.length} edge{diff.removedEdges.length !== 1 ? 's' : ''}
              </Badge>
            )}
            {diff.metadataChanges.length > 0 && (
              <Badge variant="info">
                {diff.metadataChanges.length} metadata
              </Badge>
            )}
          </div>
        )}
      </div>

      {/* No changes state */}
      {totalChanges === 0 && (
        <div className="text-center py-8 text-trunnion-slate-500">
          <GitCompare className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">These versions are identical</p>
        </div>
      )}

      {/* Metadata changes */}
      {diff.metadataChanges.length > 0 && (
        <DiffSection title="Metadata Changes" icon={<FileText className="h-4 w-4" />}>
          {diff.metadataChanges.map((change) => (
            <div
              key={change.field}
              className="rounded-lg border border-blue-500/30 bg-blue-500/5 p-3"
            >
              <div className="flex items-center gap-2 mb-1">
                <Pencil className="h-3.5 w-3.5 text-blue-400" />
                <span className="text-sm font-medium text-blue-400 capitalize">
                  {change.field}
                </span>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <span className="text-red-400 line-through">{change.oldValue}</span>
                <ArrowRight className="h-3 w-3 text-trunnion-slate-500" />
                <span className="text-green-400">{change.newValue}</span>
              </div>
            </div>
          ))}
        </DiffSection>
      )}

      {/* Added nodes */}
      {diff.addedNodes.length > 0 && (
        <DiffSection title="Added Nodes" icon={<Plus className="h-4 w-4 text-green-400" />}>
          {diff.addedNodes.map((nodeId) => (
            <div
              key={nodeId}
              className="rounded-lg border border-green-500/30 bg-green-500/5 p-3"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Plus className="h-3.5 w-3.5 text-green-400" />
                  <span className="text-sm font-medium text-green-400">
                    {getNodeLabel(versionB, nodeId)}
                  </span>
                </div>
                <Badge variant="success">{getNodeType(versionB, nodeId)}</Badge>
              </div>
            </div>
          ))}
        </DiffSection>
      )}

      {/* Removed nodes */}
      {diff.removedNodes.length > 0 && (
        <DiffSection title="Removed Nodes" icon={<Minus className="h-4 w-4 text-red-400" />}>
          {diff.removedNodes.map((nodeId) => (
            <div
              key={nodeId}
              className="rounded-lg border border-red-500/30 bg-red-500/5 p-3"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Minus className="h-3.5 w-3.5 text-red-400" />
                  <span className="text-sm font-medium text-red-400">
                    {getNodeLabel(versionA, nodeId)}
                  </span>
                </div>
                <Badge variant="error">{getNodeType(versionA, nodeId)}</Badge>
              </div>
            </div>
          ))}
        </DiffSection>
      )}

      {/* Changed nodes */}
      {diff.changedNodes.length > 0 && (
        <DiffSection title="Changed Nodes" icon={<Pencil className="h-4 w-4 text-trunnion-gold-500" />}>
          {diff.changedNodes.map((item) => (
            <div
              key={item.id}
              className="rounded-lg border border-trunnion-gold-500/30 bg-trunnion-gold-500/5 p-3"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Pencil className="h-3.5 w-3.5 text-trunnion-gold-500" />
                  <span className="text-sm font-medium text-trunnion-gold-500">
                    {getNodeLabel(versionB, item.id) || getNodeLabel(versionA, item.id)}
                  </span>
                </div>
                <Badge variant="warning">
                  {item.changes.length} change{item.changes.length !== 1 ? 's' : ''}
                </Badge>
              </div>
              <ul className="space-y-1 ml-5">
                {item.changes.map((change, idx) => (
                  <li
                    key={idx}
                    className="text-xs text-trunnion-slate-400 list-disc"
                  >
                    {change}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </DiffSection>
      )}

      {/* Added edges */}
      {diff.addedEdges.length > 0 && (
        <DiffSection title="Added Connections" icon={<Plus className="h-4 w-4 text-green-400" />}>
          {diff.addedEdges.map((edgeId) => (
            <div
              key={edgeId}
              className="rounded-lg border border-green-500/30 bg-green-500/5 p-3"
            >
              <div className="flex items-center gap-2">
                <Plus className="h-3.5 w-3.5 text-green-400" />
                <span className="text-sm text-green-400">
                  {getEdgeLabel(versionB, edgeId)}
                </span>
              </div>
            </div>
          ))}
        </DiffSection>
      )}

      {/* Removed edges */}
      {diff.removedEdges.length > 0 && (
        <DiffSection title="Removed Connections" icon={<Minus className="h-4 w-4 text-red-400" />}>
          {diff.removedEdges.map((edgeId) => (
            <div
              key={edgeId}
              className="rounded-lg border border-red-500/30 bg-red-500/5 p-3"
            >
              <div className="flex items-center gap-2">
                <Minus className="h-3.5 w-3.5 text-red-400" />
                <span className="text-sm text-red-400">
                  {getEdgeLabel(versionA, edgeId)}
                </span>
              </div>
            </div>
          ))}
        </DiffSection>
      )}
    </div>
  );
}

/** Collapsible section wrapper for diff categories */
function DiffSection({
  title,
  icon,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        {icon}
        <h4 className="text-xs font-medium text-trunnion-slate-400 uppercase tracking-wider">
          {title}
        </h4>
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}
